import { useEffect, useState } from "react";
import { Plus, PushPin, X, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * QuickNotesWidget — V1.1.1 OP-002 · Dashboard Productivity (Batch B #4).
 *
 * Personal sticky-note reminders. NOT tasks — no assignee, no due date,
 * no status. Just a low-friction space for "don't forget to call Charlie
 * back". Every note is private to its owner; the backend enforces this
 * on every read and write.
 *
 * UX: click "+" to add. Notes render as coloured cards. Click a card's
 * body to edit inline. Colour cycles on the paint icon in each card.
 */

const COLORS = ["yellow", "amber", "green", "blue", "rose", "slate"];
const TONE = {
  yellow: "bg-yellow-50 border-yellow-200",
  amber:  "bg-amber-50 border-amber-200",
  green:  "bg-emerald-50 border-emerald-200",
  blue:   "bg-sky-50 border-sky-200",
  rose:   "bg-rose-50 border-rose-200",
  slate:  "bg-slate-50 border-slate-200",
};

export default function QuickNotesWidget() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/quick-notes");
      setNotes(r.data || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      const r = await api.post("/quick-notes", { text: "", color: "yellow" });
      setNotes((prev) => [r.data, ...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    }
  };

  const update = async (id, patch) => {
    // Optimistic update — feels instant on colour cycle + text save.
    setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));
    try {
      await api.patch(`/quick-notes/${id}`, patch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save note");
      await load();
    }
  };

  const remove = async (id) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
    try {
      await api.delete(`/quick-notes/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete note");
      await load();
    }
  };

  return (
    <div data-testid="quick-notes-widget" className="rounded-md border border-divider bg-surface">
      <div className="flex items-center justify-between border-b border-divider px-4 py-2.5">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-ink-secondary">
          <PushPin size={12} weight="fill" /> Quick Notes
        </h3>
        <div className="flex items-center gap-2">
          {!loading && (
            <span className="text-xs font-medium text-ink-secondary">
              {notes.length} of 30
            </span>
          )}
          <button
            data-testid="quick-notes-add"
            onClick={add}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Plus size={11} weight="bold" /> Add
          </button>
        </div>
      </div>

      <div className="p-3">
        {loading && (
          <div className="py-6 text-center text-sm text-ink-secondary">Loading notes…</div>
        )}
        {error && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            {error}
          </div>
        )}
        {!loading && !error && notes.length === 0 && (
          <div
            data-testid="quick-notes-empty"
            className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500"
          >
            No notes yet. Click <span className="font-semibold">Add</span> to
            drop a personal reminder — private to you, not a task.
          </div>
        )}
        {!loading && notes.length > 0 && (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
            {notes.map((n) => (
              <QuickNoteCard
                key={n.id}
                note={n}
                onUpdate={(patch) => update(n.id, patch)}
                onDelete={() => remove(n.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuickNoteCard({ note, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(!note.text);
  const [text, setText] = useState(note.text || "");

  useEffect(() => { setText(note.text || ""); }, [note.text]);

  const commit = () => {
    if ((text || "").trim() !== (note.text || "").trim()) {
      onUpdate({ text: text.trim() });
    }
    setEditing(false);
  };

  const cycleColor = () => {
    const idx = COLORS.indexOf(note.color);
    const next = COLORS[(idx + 1) % COLORS.length];
    onUpdate({ color: next });
  };

  return (
    <div
      data-testid={`quick-note-${note.id}`}
      className={`rounded-md border p-2.5 shadow-sm ${TONE[note.color] || TONE.yellow}`}
    >
      <div className="flex items-start justify-between gap-1">
        <button
          data-testid={`quick-note-color-${note.id}`}
          onClick={cycleColor}
          className="rounded-md border border-white/70 bg-white/60 p-1 text-slate-600 hover:bg-white"
          title="Cycle colour"
          aria-label="Cycle colour"
        >
          <PencilSimple size={10} weight="bold" />
        </button>
        <button
          data-testid={`quick-note-delete-${note.id}`}
          onClick={onDelete}
          className="rounded-md border border-white/70 bg-white/60 p-1 text-slate-600 hover:bg-white"
          title="Delete note"
          aria-label="Delete note"
        >
          <X size={10} weight="bold" />
        </button>
      </div>

      {editing ? (
        <textarea
          data-testid={`quick-note-input-${note.id}`}
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 500))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
            if (e.key === "Escape") { setText(note.text || ""); setEditing(false); }
          }}
          rows={3}
          placeholder="Write a reminder…"
          className="mt-1 w-full resize-none bg-transparent text-sm text-slate-900 focus:outline-none"
        />
      ) : (
        <button
          data-testid={`quick-note-body-${note.id}`}
          onClick={() => setEditing(true)}
          className="mt-1 min-h-[3rem] w-full whitespace-pre-line text-left text-sm text-slate-900"
        >
          {note.text || <span className="text-slate-500 italic">Click to edit…</span>}
        </button>
      )}
    </div>
  );
}
